/**
 * A request's review and its three answers — Share, Not now, Decline — the
 * one body the review dialog and the email link's page both render. (D2)
 *
 * "Not now" is offered only while the request is not already put off; Decline
 * asks first. `onAnswered` runs once an answer is recorded.
 */
import { Loader2 } from '@tonnie/assets';
import { useTranslation } from '@tonnie/locale';
import type { MyPendingInvitation } from '@tonnie/sdk';
import { Button } from '@tonnie/ui';
import { useState } from 'react';

import { useShareRequestAnswer } from '../hooks/use-share-request-answer';
import { DeclineShareRequestDialog } from './DeclineShareRequestDialog';
import { ShareRequestReview } from './ShareRequestReview';

export function ShareRequestAnswerPanel({
  invitation,
  onAnswered,
  onBusyChange,
}: {
  invitation: MyPendingInvitation & { request: NonNullable<MyPendingInvitation['request']> };
  onAnswered: () => void;
  /** Lets a dialog refuse to close while an answer is on its way. */
  onBusyChange?: (busy: boolean) => void;
}) {
  const { t } = useTranslation();
  const company = invitation.companyName;
  const answer = useShareRequestAnswer(invitation.token, company);
  const [declineOpen, setDeclineOpen] = useState(false);
  onBusyChange?.(answer.busy);

  return (
    <div className="space-y-4">
      <ShareRequestReview request={invitation.request} company={company} />

      <div className="flex flex-wrap items-center gap-2 pt-1">
        <Button
          type="button"
          onClick={() => answer.share(onAnswered)}
          disabled={answer.busy}
          data-testid="share-request-share"
        >
          {answer.sharing ? (
            <>
              <Loader2
                className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none"
                aria-hidden="true"
              />
              {t('invitations.request.sharing')}
            </>
          ) : (
            t('invitations.request.share')
          )}
        </Button>
        {invitation.deferredUntil === null && (
          <Button
            type="button"
            variant="ghost"
            onClick={() => answer.notNow(onAnswered)}
            disabled={answer.busy}
            data-testid="share-request-not-now"
          >
            {t('invitations.card.notNow')}
          </Button>
        )}
        <Button
          type="button"
          variant="link"
          className="ml-auto text-muted-foreground"
          onClick={() => setDeclineOpen(true)}
          disabled={answer.busy}
          data-testid="share-request-decline"
        >
          {t('invitations.card.decline')}
        </Button>
      </div>

      <DeclineShareRequestDialog
        company={company}
        open={declineOpen}
        onOpenChange={setDeclineOpen}
        onConfirm={() => answer.decline(onAnswered)}
      />
    </div>
  );
}
